import type { ActiveGradeRowDTO, GradeRepairRowDTO } from '@/services/api.service';

// ============================================================
// Section « Grades actifs » de l'espace Statistiques : fonctions pures (sans I/O ni store)
// ============================================================
// Les fins viennent de LuckPerms, en secondes epoch. Une fin au-delà de l'an 3000 a été écrite en
// millisecondes par MailBoxMod : sa vraie fin est la valeur divisée par 1000 (calculée par le
// satellite, `realExpiry`). Pour LuckPerms, ce grade dure encore des dizaines de milliers d'années.

/** Noms affichés des grades connus de Paladium ; un autre grade garde son nom LuckPerms. */
const GRADE_LABELS: Record<string, string> = {
  divinite_gold: 'Divinité Gold',
  divinite: 'Divinité',
  legende: 'Légende',
  heros: 'Héros',
  premium: 'Premium',
  luckypass: 'LuckyPass',
  pionnier: 'Pionnier',
  paladin: 'Paladin',
  titan: 'Titan',
};

export function gradeLabel(grade: string): string {
  return GRADE_LABELS[grade.toLowerCase()] ?? grade;
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86_400;

/** Durée lisible : « 12 j 4 h », « 5 h 12 min », « 3 min », « moins d'une minute ». */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s >= DAY) {
    const d = Math.floor(s / DAY);
    const h = Math.floor((s % DAY) / HOUR);
    return h ? `${d} j ${h} h` : `${d} j`;
  }
  if (s >= HOUR) {
    const h = Math.floor(s / HOUR);
    const m = Math.floor((s % HOUR) / MINUTE);
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  if (s >= MINUTE) return `${Math.floor(s / MINUTE)} min`;
  return "moins d'une minute";
}

/** Date et heure en français (« 04/11/2026 14:30 ») d'une fin en secondes epoch. */
export function formatEnd(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Année d'une fin aberrante (« an 58 698 ») : la date complète ne dit rien de plus. */
export function aberrantYear(epochSeconds: number): string {
  return `an ${new Date(epochSeconds * 1000).getUTCFullYear().toLocaleString('fr-FR')}`;
}

/**
 * Fin qui compte pour le tri et le temps restant : la vraie fin d'une fin aberrante, sinon la fin
 * écrite. `null` : grade à vie. `undefined` : fin aberrante illisible.
 */
export function effectiveEnd(row: ActiveGradeRowDTO): number | null | undefined {
  if (row.expiry === undefined || row.expiry === null) return null;
  if (row.aberrant) return row.realExpiry ?? undefined;
  return row.expiry;
}

export type RowKind = 'temporaire' | 'a_vie' | 'aberrant' | 'aberrant_passe' | 'aberrant_illisible';

export function rowKind(row: ActiveGradeRowDTO, now: number): RowKind {
  const end = effectiveEnd(row);
  if (end === null) return 'a_vie';
  if (!row.aberrant) return 'temporaire';
  if (end === undefined) return 'aberrant_illisible';
  return end > now ? 'aberrant' : 'aberrant_passe';
}

/** Temps restant affiché : « 12 j 4 h », « à vie », ou ce qu'une fin aberrante aurait dû donner. */
export function remainingLabel(row: ActiveGradeRowDTO, now: number): string {
  const end = effectiveEnd(row);
  if (end === null) return 'à vie';
  if (end === undefined) return 'date illisible';
  if (end > now) return formatDuration(end - now);
  return `fini depuis ${formatDuration(now - end)}`;
}

export type SortOrder = 'remaining-asc' | 'remaining-desc' | 'name';

/** Nom de tri d'un joueur : pseudo Recube, sinon gamertag, sinon uuid (après les autres). */
export function sortName(row: { name?: string; displayName?: string; uuid: string }): string {
  return (row.displayName || row.name || `~${row.uuid}`).toLowerCase();
}

/**
 * Tri par temps restant : les fins les plus proches d'abord (`remaining-asc`), les grades à vie à la
 * fin ; à l'inverse en `remaining-desc`. Une fin aberrante compte pour sa vraie fin ; illisible, elle
 * passe après les fins connues. À égalité, par nom.
 */
export function sortRows(rows: ActiveGradeRowDTO[], order: SortOrder): ActiveGradeRowDTO[] {
  const rank = (r: ActiveGradeRowDTO): number => {
    const end = effectiveEnd(r);
    if (end === null) return Number.POSITIVE_INFINITY;
    if (end === undefined) return Number.MAX_SAFE_INTEGER;
    return end;
  };
  return [...rows].sort((a, b) => {
    if (order !== 'name') {
      const d = rank(a) - rank(b);
      if (d !== 0 && !Number.isNaN(d)) return order === 'remaining-asc' ? d : -d;
    }
    return sortName(a).localeCompare(sortName(b), 'fr');
  });
}

/** Texte sans accents ni casse, pour la recherche. */
function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Recherche par pseudo Recube, gamertag ou uuid (morceau, sans casse ni accents) et filtre par grade. */
export function filterRows<T extends { uuid: string; name?: string; displayName?: string; grade: string }>(
  rows: T[],
  query: string,
  grade: string,
): T[] {
  const q = fold(query.trim());
  return rows.filter((r) => {
    if (grade !== 'all' && r.grade !== grade) return false;
    if (!q) return true;
    return [r.displayName, r.name, r.uuid].some((v) => !!v && fold(v).includes(q));
  });
}

/** Ce que la correction fait d'un node, en une phrase. */
export function repairActionLabel(row: GradeRepairRowDTO): string {
  switch (row.action) {
    case 'CORRIGER': return 'Fin remplacée par la vraie date';
    case 'RETIRER': return 'Grade retiré : la vraie date est passée';
    default: return 'Rien : vraie date illisible';
  }
}

/** Résultat d'un node après la correction. */
export function repairStatusLabel(status: string | undefined): { text: string; ok: boolean } {
  switch (status) {
    case 'corrige': return { text: 'Corrigé', ok: true };
    case 'retire': return { text: 'Retiré', ok: true };
    case 'corrige_non_verifie': return { text: 'Corrigé (relecture impossible)', ok: true };
    case 'retire_non_verifie': return { text: 'Retiré (relecture impossible)', ok: true };
    case 'echec_retrait': return { text: 'Échec : rien n\'a changé pour ce grade', ok: false };
    case 'retire_sans_remise': return { text: 'Échec : grade retiré mais pas reposé, à reposer à la main (ancienne fin au journal)', ok: false };
    case 'prevu': return { text: 'Non traité', ok: false };
    default: return { text: status ?? '', ok: false };
  }
}
